import type { CSSProperties } from 'react';
import './DeepSyncMark.css';

type AnimVariant = 'static' | 'spin' | 'ambient';
type SizeVariant = 'solid' | 'dense' | 'regular';

type Props = {
  size?: number | string;
  /** Animation state — static (default), spin (active sync), ambient (idle pulse). */
  variant?: AnimVariant;
  /** SVG detail level — auto-selected from size when omitted. */
  detail?: SizeVariant;
  /** Ring / structural color. Defaults to currentColor. Pass an explicit light color
   *  (e.g. "#e2e8f0") when rendering on dark backgrounds. */
  color?: string;
  /** Sweep arc + accent dot color. Defaults to "#38bdf8". */
  accent?: string;
  title?: string;
  className?: string;
};

/* ── Solid variant — sizes ≤ 20px ─────────────────────────────────────────── */
function SolidMark({ color, accent }: { color: string; accent: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="9" fill={color} opacity="0.15" />
      <circle cx="12" cy="12" r="11" stroke={color} strokeWidth="1.5" opacity="0.5" />
      <g className="ds-mark__sweep">
        <path
          d="M 12 1 A 11 11 0 0 1 21.5 7.5"
          stroke={accent}
          strokeWidth="2"
          strokeLinecap="round"
        />
      </g>
      <circle cx="12" cy="12" r="3.5" fill={color} />
      <circle cx="12" cy="12" r="1.5" fill={accent} />
    </svg>
  );
}

/* ── Dense variant — sizes 20–48px ────────────────────────────────────────── */
function DenseMark({ color, accent }: { color: string; accent: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none">
      <circle cx="24" cy="24" r="20" stroke={color} strokeWidth="1.5" opacity="0.4" />
      <circle cx="24" cy="24" r="14" stroke={color} strokeWidth="1.5" opacity="0.65" />
      <g className="ds-mark__sweep">
        <path
          d="M 24 4 A 20 20 0 0 1 41.3 15.5"
          stroke={accent}
          strokeWidth="2.5"
          strokeLinecap="round"
        />
        <circle cx="41.3" cy="15.5" r="2" fill={accent} />
      </g>
      <circle cx="24" cy="24" r="3.5" fill={color} />
      <circle cx="24" cy="24" r="1.5" fill={accent} />
    </svg>
  );
}

/* ── Regular variant — sizes ≥ 48px ───────────────────────────────────────── */
function RegularMark({ color, accent }: { color: string; accent: string }) {
  return (
    <svg viewBox="0 0 96 96" fill="none">
      <circle cx="48" cy="48" r="42" stroke={color} strokeWidth="1.5" opacity="0.25" />
      <circle cx="48" cy="48" r="32" stroke={color} strokeWidth="1.5" opacity="0.4" />
      <circle cx="48" cy="48" r="22" stroke={color} strokeWidth="1.5" opacity="0.65" />
      {/* crosshair ticks */}
      <line x1="48" y1="0"  x2="48" y2="8"  stroke={color} strokeWidth="1.5" opacity="0.4" />
      <line x1="48" y1="88" x2="48" y2="96" stroke={color} strokeWidth="1.5" opacity="0.4" />
      <line x1="0"  y1="48" x2="8"  y2="48" stroke={color} strokeWidth="1.5" opacity="0.4" />
      <line x1="88" y1="48" x2="96" y2="48" stroke={color} strokeWidth="1.5" opacity="0.4" />
      <g className="ds-mark__sweep">
        <path
          d="M 48 6 A 42 42 0 0 1 86.8 31"
          stroke={accent}
          strokeWidth="4"
          strokeLinecap="round"
        />
        <circle cx="86.8" cy="31" r="3.2" fill={accent} />
      </g>
      <circle cx="48" cy="48" r="7" fill={color} />
      <circle cx="48" cy="48" r="2.6" fill={accent} />
    </svg>
  );
}

/** Pick detail level from numeric size. */
function autoDetail(size: number | string): SizeVariant {
  const n = typeof size === 'number' ? size : parseInt(String(size), 10);
  if (isNaN(n) || n <= 20) return 'solid';
  if (n <= 48) return 'dense';
  return 'regular';
}

export function DeepSyncMark({
  size = 24,
  variant = 'static',
  detail,
  color = 'currentColor',
  accent = '#38bdf8',
  title,
  className = '',
}: Props) {
  const d = detail ?? autoDetail(size);
  const cls = `ds-mark ds-mark--${variant} ${className}`.trim();

  return (
    <span
      className={cls}
      style={{ width: size, height: size } as CSSProperties}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    >
      {d === 'solid' && <SolidMark color={color} accent={accent} />}
      {d === 'dense' && <DenseMark color={color} accent={accent} />}
      {d === 'regular' && <RegularMark color={color} accent={accent} />}
    </span>
  );
}
